import type { AssetSourceType } from "./MarathonImage"

/**
 * Maps media channel names to AssetSourceType enum values
 *
 * @param mediaChannel - The media channel from Cube queries (e.g., "facebook", "instagram", "tiktok")
 * @param isOrganic - When true, maps tiktok/youtube to their organic source types (TIKTOK, YOUTUBE)
 *                    instead of paid (TIKTOK_AD, YOUTUBE_AD). Defaults to false (paid context).
 * @returns The corresponding AssetSourceType enum value
 *
 * @example
 * ```tsx
 * // Paid context (default):
 * mapMediaChannelToSourceType("tiktok")        // -> "TIKTOK_AD"
 * mapMediaChannelToSourceType("youtube")       // -> "YOUTUBE_AD"
 *
 * // Organic context:
 * mapMediaChannelToSourceType("tiktok", true)  // -> "TIKTOK"
 * mapMediaChannelToSourceType("youtube", true) // -> "YOUTUBE"
 * ```
 */
export function mapMediaChannelToSourceType(
  mediaChannel: string,
  isOrganic = false,
): AssetSourceType {
  const channelMap: Record<string, AssetSourceType> = {
    facebook: "FACEBOOK_AD",
    meta: "FACEBOOK_AD",
    instagram: isOrganic ? "INSTAGRAM" : "FACEBOOK_AD",
    tiktok: isOrganic ? "TIKTOK" : "TIKTOK_AD",
    tiktok_ads: "TIKTOK_AD",
    youtube: isOrganic ? "YOUTUBE" : "YOUTUBE_AD",
    youtube_ads: "YOUTUBE_AD",
    shopify: "SHOPIFY_PRODUCT",
    upload: "UPLOAD",
    gen_ai: "GEN_AI",
  }

  const normalized = mediaChannel?.toLowerCase().trim()
  return channelMap[normalized] || "FACEBOOK_AD"
}

/**
 * Type guard to check if a value is a valid AssetSourceType
 */
export function isValidAssetSourceType(
  value: string,
): value is AssetSourceType {
  const validTypes: AssetSourceType[] = [
    "FACEBOOK_AD",
    "TIKTOK_AD",
    "SHOPIFY_PRODUCT",
    "GEN_AI",
    "UPLOAD",
    "INSTAGRAM",
    "TIKTOK",
    "YOUTUBE",
    "YOUTUBE_AD",
  ]
  return validTypes.includes(value as AssetSourceType)
}

/** Extensions rendered with a <video> element instead of <img>. */
const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "m4v", "3gp", "ogv"]

/**
 * Detect whether a URL points at a video file (e.g. a Meta ad MP4) by its
 * path extension, ignoring the query string — used both to pick <video> over
 * <img> and to send a cached "preview" that is really a video as the blast's
 * video fallback rather than its image.
 */
export function isVideoUrl(url: string): boolean {
  let pathname = url
  try {
    pathname = new URL(url).pathname
  } catch {
    pathname = url.split("?")[0].split("#")[0]
  }
  const ext = pathname.split(".").pop()?.toLowerCase()
  return !!ext && VIDEO_EXTENSIONS.includes(ext)
}
